import React from 'react';
import { useQuota } from '../context/QuotaContext';
import { useDeals } from '../context/DealsContext';
import { FiTarget, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const QuotaProgress = ({ periodOverride }) => {
  const { getAdjustedQuota, getPipelineGoal, quota } = useQuota();
  const { deals } = useDeals();

  // Use passed period or default to the one in context
  const period = periodOverride || quota.period;

  // Get quota for the current period
  const currentQuota = getAdjustedQuota(period);

  // Get pipeline goal (4x quota)
  const pipelineGoal = getPipelineGoal(period);

  // Calculate total pipeline value
  const totalPipelineValue = deals.reduce((sum, deal) => {
    // Only count deals that aren't closed lost
    if (deal.stage !== 'Closed Lost') {
      return sum + deal.value;
    }
    return sum;
  }, 0);

  // Calculate weighted pipeline value (value * probability)
  const weightedPipelineValue = deals.reduce((sum, deal) => {
    if (deal.stage !== 'Closed Lost') {
      return sum + (deal.value * (deal.probability / 100));
    }
    return sum;
  }, 0);

  // Calculate percentages
  const quotaPercentage = Math.min(100, (weightedPipelineValue / currentQuota) * 100);
  const pipelinePercentage = Math.min(100, (totalPipelineValue / pipelineGoal) * 100);

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Determine CSS classes based on percentage
  const getQuotaClasses = () => {
    if (quotaPercentage >= 75) {
      return {
        text: "text-green-700 dark:text-green-500",
        bg: "bg-green-600 dark:bg-green-500"
      };
    } else if (quotaPercentage >= 50) {
      return {
        text: "text-amber-700 dark:text-amber-500",
        bg: "bg-amber-600 dark:bg-amber-500"
      };
    } else {
      return {
        text: "text-red-700 dark:text-red-500",
        bg: "bg-red-600 dark:bg-red-500"
      };
    }
  };

  const getPipelineClasses = () => {
    if (pipelinePercentage >= 75) {
      return {
        text: "text-green-700 dark:text-green-500",
        bg: "bg-green-600 dark:bg-green-500"
      };
    } else if (pipelinePercentage >= 50) {
      return {
        text: "text-amber-700 dark:text-amber-500",
        bg: "bg-amber-600 dark:bg-amber-500"
      };
    } else {
      return {
        text: "text-red-700 dark:text-red-500",
        bg: "bg-red-600 dark:bg-red-500"
      };
    }
  };

  const quotaClasses = getQuotaClasses();
  const pipelineClasses = getPipelineClasses();

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize text-center">
        {period} Quota & Pipeline Goal
      </h3>
      <div className="space-y-8">
        {/* Quota Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 w-full text-center">
              <SafeIcon icon={FiTarget} className={`${quotaClasses.text} sm:mr-2`} />
              <span className="font-medium text-gray-800 dark:text-gray-200 capitalize text-center w-full">
                {period} Quota: {formatCurrency(currentQuota)}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 hidden sm:block">
              {formatCurrency(weightedPipelineValue)} ({Math.round(quotaPercentage)}%)
            </span>
          </div>
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 text-center sm:hidden mb-2">
            {formatCurrency(weightedPipelineValue)} ({Math.round(quotaPercentage)}%)
          </div>
          <div className="w-full h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-5 rounded-full transition-all duration-500 ${quotaClasses.bg} flex items-center justify-end pr-2`}
              style={{ width: `${quotaPercentage}%` }}
            >
              {quotaPercentage >= 25 && (
                <span className="text-xs font-medium text-white">
                  {Math.round(quotaPercentage)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>Weighted Pipeline</span>
            <span>100%</span>
          </div>
        </div>

        {/* Pipeline Goal Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 w-full text-center">
              <SafeIcon icon={FiTrendingUp} className={`${pipelineClasses.text} sm:mr-2`} />
              <span className="font-medium text-gray-800 dark:text-gray-200 capitalize text-center w-full">
                Pipeline Goal (4x): {formatCurrency(pipelineGoal)}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 hidden sm:block">
              {formatCurrency(totalPipelineValue)} ({Math.round(pipelinePercentage)}%)
            </span>
          </div>
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 text-center sm:hidden mb-2">
            {formatCurrency(totalPipelineValue)} ({Math.round(pipelinePercentage)}%)
          </div>
          <div className="w-full h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-5 rounded-full transition-all duration-500 ${pipelineClasses.bg} flex items-center justify-end pr-2`}
              style={{ width: `${pipelinePercentage}%` }}
            >
              {pipelinePercentage >= 25 && (
                <span className="text-xs font-medium text-white">
                  {Math.round(pipelinePercentage)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>Total Pipeline</span>
            <span>100%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaProgress;